import type { Logout } from 'flowhawk-core';
import type { Reducer } from '@reduxjs/toolkit';

import type { ResetData } from './ApplicationActions';
import type { AuthorizedEntity, FeaturePermissions } from '../Utils/types';
import { DEFAULT_FEATURE_PERMISSIONS } from '../Utils/types';

export type PermissionState = {
  featurePermissions: FeaturePermissions;
  // Whether feature permissions have been loaded from the server at least once.
  // Consumers use this to stay permissive before the first load completes.
  featurePermissionsLoaded: boolean;
};

const initialState: PermissionState = {
  featurePermissions: DEFAULT_FEATURE_PERMISSIONS,
  featurePermissionsLoaded: false,
};

export type GetFeaturePermissions = { type: 'Permission/GET_FEATURE_PERMISSIONS' };
export type StorePermissions = {
  type: 'Permission/STORE_PERMISSIONS';
  featurePermissions: FeaturePermissions;
};

export type AddPermission = {
  type: 'Permission/ADD';
  entityType: string;
  entityId: number;
  subEntityType?: string;
  subEntityId?: number;
  cascade: boolean;
  read: boolean;
  update: boolean;
  canDelete: boolean;
  authorizedType: string;
  authorizedId: number;
  displayName: string;
  displayToast: boolean;
};

export type UpdatePermission = {
  type: 'Permission/UPDATE';
  entityType: string;
  entityId: number;
  permissionId: number;
  read: boolean;
  update: boolean;
  canDelete: boolean;
};

export type AddPermissionToAuthorizedEntities = {
  type: 'Permission/ADD_TO_AUTHORIZED_ENTITIES';
  authorizedEntities: Array<AuthorizedEntity>;
  entityType: string;
  entityId: number;
  subEntityType?: string;
  subEntityId?: number;
  cascade: boolean;
  read: boolean;
  update: boolean;
  canDelete: boolean;
  displayToast: boolean;
};

type Action =
  | GetFeaturePermissions
  | StorePermissions
  | AddPermission
  | UpdatePermission
  | AddPermissionToAuthorizedEntities
  | Logout
  | ResetData;

export const getFeaturePermissions = (): GetFeaturePermissions => ({ type: 'Permission/GET_FEATURE_PERMISSIONS' });
export const storePermissions = (featurePermissions: FeaturePermissions): StorePermissions => ({
  type: 'Permission/STORE_PERMISSIONS',
  featurePermissions,
});

export const addPermissionToAuthorizedEntities = (
  authorizedEntities: Array<AuthorizedEntity>,
  entityType: string,
  entityId: number,
  subEntityType: string | undefined,
  subEntityId: number | undefined,
  cascade: boolean,
  read: boolean,
  update: boolean,
  canDelete: boolean,
  displayToast: boolean,
): AddPermissionToAuthorizedEntities => ({
  type: 'Permission/ADD_TO_AUTHORIZED_ENTITIES',
  authorizedEntities,
  entityType,
  entityId,
  subEntityType,
  subEntityId,
  cascade,
  read,
  update,
  canDelete,
  displayToast,
});

export const addPermission = (
  entityType: string,
  entityId: number,
  subEntityType: string | undefined,
  subEntityId: number | undefined,
  cascade: boolean,
  read: boolean,
  update: boolean,
  canDelete: boolean,
  authorizedType: string,
  authorizedId: number,
  displayName: string,
  displayToast: boolean,
): AddPermission => ({
  type: 'Permission/ADD',
  entityType,
  entityId,
  subEntityType,
  subEntityId,
  cascade,
  read,
  update,
  canDelete,
  authorizedType,
  authorizedId,
  displayName,
  displayToast,
});

export const updatePermission = (
  entityType: string,
  entityId: number,
  permissionId: number,
  read: boolean,
  update: boolean,
  canDelete: boolean,
): UpdatePermission => ({
  type: 'Permission/UPDATE',
  entityType,
  permissionId,
  entityId,
  read,
  update,
  canDelete,
});

const reducer = (state: PermissionState = initialState, action: Action): PermissionState => {
  switch (action.type) {
    case 'Application/RESET_DATA':
    case 'Auth/LOGOUT':
      // Reset the store when logging out
      return {
        ...initialState,
      };

    case 'Permission/STORE_PERMISSIONS':
      return {
        ...state,

        featurePermissions: action.featurePermissions,
        featurePermissionsLoaded: true,
      };

    default:
      return state;
  }
};

export default reducer as Reducer<PermissionState>;
